import type { Messages } from "@/i18n/locales/en";
import { legalMessagesDe } from "@/i18n/locales/legalDeData";
import { pagesMessagesDe } from "@/i18n/locales/pagesDeData";

export const deMessages = {
  meta: {
    description:
      "WasOK — Produkte scannen, Bewertungen und Notizen speichern, merken was sich gelohnt hat.",
    title: "WasOK — Merken, was sich gelohnt hat",
  },
  header: {
    navFeatures: "Funktionen",
    navHowItWorks: "So funktioniert's",
    navPrivacy: "Datenschutz",
    navUseCases: "Anwendungsfälle",
    download: "Download",
    openMenu: "Menü öffnen",
    closeMenu: "Menü schließen",
    languageAria: "Sprache",
  },
  hero: {
    badge: "📱 Jetzt auf Android verfügbar",
    title: "Merken, was sich gelohnt hat.",
    subtitle:
      "Produkte scannen, Bewertungen und Notizen speichern — und sofort wissen, ob ein Produkt wirklich gut war.",
    googlePlay: "Bei Google Play herunterladen",
    watchDemo: "Demo ansehen",
    privacyLine: "Alle Daten bleiben auf deinem Gerät.",
    phoneTitle: "WasOK",
    phoneSubtitle: "Scannen zum Merken",
    snackCard: "Lecker!",
    coffeeCard: "Zu bitter",
    scrollHint: "Scrollen zum Entdecken",
  },
  problems: {
    heading: "Kommt dir bekannt vor?",
    subheading:
      "Wir kennen alle den Moment — vor dem Regal stehen und überlegen, ob es letztes Mal gut war.",
    notedAgo: "✓ Du hast das vor 3 Monaten notiert",
    items: [
      {
        question: "Welche Schokolade hat mir wirklich geschmeckt?",
        note: "Perfekte Süße",
      },
      {
        question: "Warum kaufe ich dieses Shampoo nicht mehr?",
        note: "Hat die Haare ausgetrocknet",
      },
      {
        question: "War dieser Kaffee gut oder enttäuschend?",
        note: "Zu bitter",
      },
    ],
  },
  howItWorks: {
    heading: "So funktioniert's",
    subheading:
      "Vier einfache Schritte, damit du deine Produktmeinungen nicht vergisst.",
    stepPrefix: "Schritt",
    steps: [
      {
        title: "Scanne jeden Produkt-Barcode",
        description:
          "Schnell und einfach mit der Kamera. Funktioniert mit jedem Barcode.",
      },
      {
        title: "Speichere Bewertungen, Kommentare & Fotos",
        description:
          "Bewerte Produkte, füge Notizen, Fotos, Preise und Orte hinzu.",
      },
      {
        title: "Erinnere dich sofort an deine Erfahrung",
        description:
          "Beim erneuten Scannen siehst du frühere Bewertung und Notizen.",
      },
      {
        title: "Erstelle smarte Einkaufslisten",
        description:
          "Listen aus Produkten, denen du bereits vertraust.",
      },
    ],
  },
  privacy: {
    heading: "Deine Einkaufshistorie bleibt deins.",
    body: "WasOK hilft dir, Produkte in Erinnerung zu behalten, die du ausprobiert hast, und speichert Bewertungen, Notizen, Fotos und Einkaufslisten lokal auf deinem Gerät.",
    trustBanner:
      "💚 Rund um deine Einkaufshistorie — nicht rund um das Sammeln deiner Daten.",
    bullets: [
      "Kein Konto nötig",
      "Keine Cloud-Synchronisation nötig",
      "Lokale Speicherung",
      "Backups unter deiner Kontrolle",
      "Optionale Barcode-Suche für Produktinformationen.",
      "Werbefinanziert mit Google AdMob",
    ],
  },
  features: {
    heading: "Alles, was du brauchst",
    subheading:
      "Starke Funktionen für einfaches, privates Produkt-Tracking.",
    items: [
      {
        title: "Barcode-Scanner",
        description: "Schnelles, genaues Scannen für jedes Produkt.",
      },
      {
        title: "Produktbewertungen",
        description: "1–5 Sterne, um dir die Qualität zu merken.",
      },
      {
        title: "Produktfotos",
        description: "Fotos für Verpackung oder Details anhängen.",
      },
      {
        title: "Kategorien",
        description: "Organisiere Produkte in eigenen Kategorien.",
      },
      {
        title: "Geschäft",
        description: "Merke dir, wo du jedes Produkt gekauft hast.",
      },
      {
        title: "Preise",
        description: "Preise tracken und beste Angebote finden.",
      },
      {
        title: "Einkaufslisten",
        description: "Listen aus Lieblingsprodukten erstellen.",
      },
      {
        title: "Offline",
        description: "Funktioniert auch ohne Internet.",
      },
      {
        title: "Lokale Speicherung",
        description: "Alle Daten sicher auf deinem Gerät.",
      },
    ],
  },
  useCases: {
    heading: "Für jedes Regal",
    subheading:
      "Lebensmittel, Beauty oder Haushalt — WasOK hilft dir zu merken.",
    cards: [
      {
        emoji: "🍫",
        title: "Snacks & Süßes",
        description: "Merke dir, welche Süßigkeiten wirklich gut waren.",
      },
      {
        emoji: "☕",
        title: "Kaffee & Getränke",
        description: "Markiere Kaffees, die sich wieder lohnen.",
      },
      {
        emoji: "💄",
        title: "Kosmetik & Pflege",
        description: "Notizen zu Hautreaktionen.",
      },
      {
        emoji: "🧹",
        title: "Haushalt",
        description: "Nichts mehr kaufen, was enttäuscht hat.",
      },
    ],
  },
  screenshots: {
    heading: "Schön & intuitiv",
    subheading:
      "Ein durchdachtes Interface, das Tracking zum Vergnügen macht.",
    items: [
      { title: "Scanner" },
      { title: "Produktdetail" },
      { title: "Bewertung" },
      { title: "Suche & Verlauf" },
      { title: "Einkaufsliste" },
    ],
  },
  cta: {
    heading: "Einkaufsgedächtnis fürs echte Leben.",
    body: "Vergiss nicht mehr, welche Produkte sich gelohnt haben. Shoppe heute smarter.",
    googlePlay: "Bei Google Play herunterladen",
    scanQr: "Scannen zum Download",
    tagline: "Privat · Offline · Einfach",
  },
  footer: {
    tagline: "Merken, was sich gelohnt hat.",
    linksHeading: "Links",
    privacyPolicy: "Datenschutz",
    terms: "Nutzungsbedingungen",
    contact: "Kontakt",
    linkFaq: "FAQ",
    linkLicenses: "Open-Source-Lizenzen",
    linkAdvertising: "Werbung & Monetarisierung",
    downloadHeading: "Download",
    googlePlay: "Google Play",
    copyright:
      "© 2026 WasOK. Alle Rechte vorbehalten. Mit 💚 für smarteres Einkaufen.",
  },
  legal: legalMessagesDe,
  faq: pagesMessagesDe.faq,
  support: pagesMessagesDe.support,
  advertising: pagesMessagesDe.advertising,
} satisfies Messages;
